import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Timeline, Progress, Tag, Spin, Alert } from 'antd';
import { 
  ArrowUpOutlined, 
  ArrowDownOutlined, 
  DatabaseOutlined,
  FileTextOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { dashboardApi, dataProcessingApi } from '../services/api';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState({});
  const [recentActivities, setRecentActivities] = useState([]);
  const [processingHistory, setProcessingHistory] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // 获取仪表盘统计数据
      const statsResponse = await dashboardApi.getStats();
      setDashboardStats(statsResponse || {});
      
      // 获取最近活动
      const activitiesResponse = await dashboardApi.getRecentActivities(5);
      setRecentActivities(activitiesResponse || []);
      
      // 获取处理历史
      const historyResponse = await dashboardApi.getProcessingHistory(3);
      setProcessingHistory(historyResponse || []);
      
    } catch (error) {
      console.error('获取仪表盘数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <Spin size="large" />
      </div>
    );
  }

  const { sku_configs = {}, data_processing = {}, system = {} } = dashboardStats;

  return (
    <div className="p-6">
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="SKU配置总数"
              value={sku_configs.total || 0}
              valueStyle={{ color: '#3f8600' }}
              prefix={<SettingOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="激活配置"
              value={sku_configs.active || 0}
              valueStyle={{ color: '#1890ff' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="已处理数据行数"
              value={data_processing.total_rows || 0}
              valueStyle={{ color: '#722ed1' }}
              prefix={<DatabaseOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="处理成功率"
              value={data_processing.success_rate || 0}
              precision={1}
              valueStyle={{ color: data_processing.success_rate >= 90 ? '#3f8600' : '#cf1322' }}
              prefix={data_processing.success_rate >= 90 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
              suffix="%"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="mt-6">
        <Col xs={24} lg={12}>
          <Card title="最近活动" extra={<Tag color="blue">实时更新</Tag>}>
            {recentActivities.length > 0 ? (
              <Timeline>
                {recentActivities.map((activity, index) => (
                  <Timeline.Item
                    key={activity.id}
                    color={activity.status === 'active' ? 'green' : 'blue'}
                    dot={activity.type === 'data_processing' ? <FileTextOutlined /> : <SettingOutlined />}
                  >
                    <div>
                      <div className="font-medium">{activity.title}</div>
                      <div className="text-gray-500 text-sm">{activity.description}</div>
                      <div className="text-gray-400 text-xs">
                        {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : ''}
                      </div>
                    </div>
                  </Timeline.Item>
                ))}
              </Timeline>
            ) : (
              <div className="text-center text-gray-500 py-4">暂无活动记录</div>
            )}
          </Card>
        </Col>
        
        <Col xs={24} lg={12}>
          <Card title="数据处理状态" extra={<Tag color="green">运行中</Tag>}>
            {data_processing.has_data ? (
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span>处理进度</span>
                    <span>{data_processing.success_rate}%</span>
                  </div>
                  <Progress 
                    percent={data_processing.success_rate} 
                    status={data_processing.success_rate >= 90 ? 'success' : 'normal'}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">总行数</div>
                    <div className="font-medium">{data_processing.total_rows}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">总列数</div>
                    <div className="font-medium">{data_processing.total_columns}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">处理时间</div>
                    <div className="font-medium">{data_processing.processing_time?.toFixed(2)}s</div>
                  </div>
                  <div>
                    <div className="text-gray-500">最后处理</div>
                    <div className="font-medium">
                      {data_processing.last_processed ? 
                        new Date(data_processing.last_processed).toLocaleString() : 
                        '无'
                      }
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <Alert
                message="暂无数据"
                description="请先导入Excel文件进行数据处理"
                type="info"
                showIcon
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;